import React, { useEffect, useRef, useState } from "react";
import { getGooglePlaceImage } from "../../utils/googlePlaceImage";
import JournimLogo from "../LogoPlaceholder/JournimLogo";
import "./GooglePlaceImage.css";

const GooglePlaceImage = ({
  placeName,
  district,
  stateName,
  fallbackSrc,    // Usually the R2 image from DB
  placeholderSrc, // Final fallback image
  alt,
  className,
  style,
  maxWidth = 800,
  enabled = true,
  lazy = true,
}) => {
  const [imgSrc, setImgSrc] = useState(fallbackSrc || placeholderSrc || "");
  const [inView, setInView] = useState(!lazy);
  const [loadStage, setLoadStage] = useState('initial'); // 'initial' -> 'google' -> 'fallback' -> 'placeholder'
  const [googleUrls, setGoogleUrls] = useState([]);
  const [googleIdx, setGoogleIdx] = useState(0);
  const [shouldFetchGoogle, setShouldFetchGoogle] = useState(!fallbackSrc);
  // Tracks whether the browser has actually finished downloading the current
  // imgSrc — separate from imgSrc just being *set*, so the skeleton stays up
  // for the real network fetch instead of disappearing the instant a (still
  // loading) URL is assigned.
  const [imageLoaded, setImageLoaded] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    // If fallbackSrc changes, reset
    if (loadStage !== 'google') {
      setImgSrc(fallbackSrc || placeholderSrc || "");
      setShouldFetchGoogle(!fallbackSrc);
      setLoadStage('initial');
    }
  }, [fallbackSrc, placeholderSrc, loadStage]);

  useEffect(() => {
    setImageLoaded(false);
  }, [imgSrc]);

  useEffect(() => {
    if (!lazy || !imgRef.current) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "250px" }
    );

    observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, [lazy]);

  useEffect(() => {
    let cancelled = false;

    const fetchImage = async () => {
      if (!enabled || !inView || !placeName || !shouldFetchGoogle) return;

      const results = await getGooglePlaceImage({
        placeName,
        district,
        stateName,
        maxWidth,
      });

      if (!cancelled && results && results.length > 0) {
        setGoogleUrls(results);
        setGoogleIdx(0);
        setImgSrc(results[0]);
        setLoadStage('google');
      } else if (!cancelled) {
        // If Google fetch fails, set loadStage to placeholder so we don't try again
        setLoadStage('placeholder');
      }
    };

    fetchImage();

    return () => {
      cancelled = true;
    };
  }, [enabled, inView, placeName, district, stateName, maxWidth, shouldFetchGoogle]);

  const handleError = (e) => {
    if (e.currentTarget.dataset.fallbackApplied === 'true') return;

    if (loadStage === 'google') {
      // Current Google image failed, try next one in the array
      const nextIdx = googleIdx + 1;
      if (nextIdx < googleUrls.length) {
        setGoogleIdx(nextIdx);
        setImgSrc(googleUrls[nextIdx]);
        console.log(`[GooglePlaceImage] Google photo ${googleIdx} failed, trying ${nextIdx}/${googleUrls.length}`);
      } else {
        // All Google photos failed, try R2
        setImgSrc(fallbackSrc || placeholderSrc || "");
        setLoadStage('fallback');
        console.log(`[GooglePlaceImage] All ${googleUrls.length} Google photos failed, falling back to R2/Local`);
      }
    } else if (loadStage === 'initial') {
      // fallbackSrc (R2/DB) failed to load, so trigger Google Places API fetch!
      console.log(`[GooglePlaceImage] fallbackSrc failed to load for "${placeName}". Triggering Google Places lookup.`);
      setShouldFetchGoogle(true);
      // Temporarily set to placeholder while Google loads
      setImgSrc(placeholderSrc || "");
    } else if (loadStage === 'fallback') {
      // R2 failed again, try placeholder
      if (imgSrc !== placeholderSrc) {
        setImgSrc(placeholderSrc || "");
        setLoadStage('placeholder');
      } else {
        e.currentTarget.dataset.fallbackApplied = 'true';
      }
    } else if (loadStage === 'placeholder') {
      // Even placeholder failed, stop the loop
      e.currentTarget.dataset.fallbackApplied = 'true';
    }
  };

  if (loadStage === 'placeholder') {
    return <JournimLogo className={className} style={style} />;
  }

  return (
    <div className={`gpi-wrap ${className || ""}`} style={style}>
      {!imageLoaded && <div className={`gpi-skeleton ${imageLoaded ? "fade-out" : ""}`} aria-hidden="true" />}
      <img
        ref={imgRef}
        src={imgSrc}
        alt={alt || placeName || "place"}
        className={`gpi-img ${imageLoaded ? "is-loaded" : "is-loading"}`}
        // Place grids render dozens of these; let the browser defer offscreen
        // ones instead of decoding the whole list up front.
        loading="lazy"
        decoding="async"
        onLoad={() => setImageLoaded(true)}
        onError={handleError}
      />
    </div>
  );
};

export default GooglePlaceImage;
